import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { uploadMediaFile } from '../../../../actions/mediaUpload';
import {
  classifyMediaParam,
  isMediaName,
  MEDIA_KIND_META,
  mediaKindOfFile,
} from '../../createScreen/utils/parameterUtils';
import { enhancePrompt } from '../../../../actions/promptEnhance';
import { catColors } from '../../../../components/app-shell/catColors';
import { UploadIcon, CaretIcon, HistoryIcon, RefreshIcon, CloseIcon } from '../../../../components/app-shell/icons';

/* ─── helpers ──────────────────────────────────────────────────── */

const PROMPT_HISTORY_KEY = 'zen_prompt_history';
const PROMPT_HISTORY_MAX = 25;

const formatLabel = (name, title) => {
  const base = title || name;
  return base.replace(/[0-9]+$/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    .replace(/Url/g, 'URL').replace(/Urls/g, 'URLs').trim();
};

function getModelCategory(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('video')) return 'Video';
  if (t.includes('text') || t.includes('llm')) return 'Text';
  if (t.includes('workflow')) return 'Workflow';
  return 'Image';
}

function parseConditionRule(rule) {
  if (!rule) return null;
  if (typeof rule === 'string') { try { return JSON.parse(rule); } catch { return null; } }
  return rule;
}

function hasValue(v) {
  if (v === null || v === undefined || v === '' || v === 'null') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

function parseAllowedValues(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [p]; }
    catch { return raw.split(',').map(v => v.trim()); }
  }
  return [raw];
}

/** Text-ish check that survives numbers/booleans living in the same map. */
function filled(v) {
  if (typeof v === 'string') return v.trim().length > 0;
  return v !== undefined && v !== null && v !== '';
}

export function readPromptHistory() {
  try {
    const raw = JSON.parse(localStorage.getItem(PROMPT_HISTORY_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter(v => typeof v === 'string') : [];
  } catch { return []; }
}

export function pushPromptHistory(prompt) {
  const p = typeof prompt === 'string' ? prompt.trim() : '';
  if (!p) return readPromptHistory();
  const next = [p, ...readPromptHistory().filter(x => x !== p)].slice(0, PROMPT_HISTORY_MAX);
  try { localStorage.setItem(PROMPT_HISTORY_KEY, JSON.stringify(next)); } catch { /* quota — non-critical */ }
  return next;
}

/* ─── sub-components ──────────────────────────────────────────── */

function SectionLabel({ children, right }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase' }}>{children}</span>
      {right && <span style={{ fontSize: 11, color: 'var(--text-4)', flexShrink: 0 }}>{right}</span>}
    </div>
  );
}

function SelectDropdown({ param, value, onChange, disabled, restricted }) {
  const [open, setOpen] = useState(false);
  const allowedValues = restricted?.allowedValues || parseAllowedValues(param.allowed_values);
  const display = value || allowedValues[0] || '—';

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => !disabled && setOpen(v => !v)}
        disabled={disabled}
        style={{
          width: '100%', height: 34, padding: '0 10px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
          background: 'rgba(255,255,255,.04)', border: `1px solid ${disabled ? 'rgba(255,255,255,.04)' : 'var(--border)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: disabled ? 'var(--text-4)' : 'var(--text-2)',
        }}
      >
        <span className="mono">{display}</span>
        <CaretIcon s={10} />
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, maxHeight: 260, overflowY: 'auto', background: 'rgba(12,12,20,.97)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,.6)', zIndex: 200 }}>
            {allowedValues.map(v => (
              <button key={v} onClick={() => { onChange(v); setOpen(false); }}
                style={{ width: '100%', padding: '8px 12px', background: v === display ? 'rgba(255,255,255,.06)' : 'transparent', border: 'none', color: v === display ? '#fff' : 'var(--text-2)', fontSize: 12, textAlign: 'left', cursor: 'pointer' }}
              >{String(v)}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── main component ───────────────────────────────────────────── */

export default function ZenControlPanel({
  selectedModel,
  modelsLoading,
  onOpenModelSelector,
  onOpenSettings,
  textInputs,
  setTextInputs,
  uploadedImages,
  setUploadedImages,
  settings,
  setSettings,
  isGenerating,
  loadingDetails,
  onGenerate,
  formatCost,
  balance,
  showNotification,
  batchCount = 1,
  setBatchCount,
  /** Mobile renders the panel inside a sheet and supplies a close affordance. */
  onCloseSheet,
  style,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploading, setUploading] = useState(null); // paramName being uploaded
  const [dragParam, setDragParam] = useState(null);  // paramName currently hovered by a drag
  const [enhancing, setEnhancing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(() => readPromptHistory());
  const fileInputRef = useRef(null);
  const fileTargetParamRef = useRef(null);
  const rootRef = useRef(null);

  /* ── Derived params from model ────────────────────────────────── */

  const textParams = useMemo(() => {
    if (!selectedModel?.parameters) return [];
    return selectedModel.parameters.filter(p => {
      const n = p.parameter_name.toLowerCase();
      return p.data_type === 'string' && !isMediaName(n)
        && !n.includes('webhook') && !p.allowed_values && !p.is_private;
    });
  }, [selectedModel]);

  const selectParams = useMemo(() => {
    if (!selectedModel?.parameters) return [];
    const dp = selectedModel?.resolution?.dimension_presets;
    const hasDimConfig = dp?.ratios && dp?.lookup;
    return selectedModel.parameters.filter(p => {
      const n = p.parameter_name.toLowerCase();
      if (!p.allowed_values || p.is_private) return false;
      if (p.data_type !== 'string') return false;
      if (isMediaName(n) || n.includes('webhook')) return false;
      if (hasDimConfig && (n === 'aspect_ratio' || n === 'resolution')) return false;
      return true;
    });
  }, [selectedModel]);

  // Media upload slots. `mediaParams` is local to this component — the
  // uploadedImages/imageParams PROP names are left alone on purpose; renaming
  // them crosses ZenScreen/SettingsModal/GenerationCard and a missed site
  // silently drops the param from buildModelParams, i.e. the user generates
  // without their reference and still pays for it.
  const mediaParams = useMemo(() => {
    if (!selectedModel?.parameters) return [];
    return selectedModel.parameters.reduce((acc, p) => {
      const m = classifyMediaParam(p);
      if (!m) return acc;
      acc.push({
        paramName: p.parameter_name,
        title: formatLabel(p.parameter_name, p.parameter_title),
        kind: m.kind,
        isArray: m.isArray,
        max: m.max,
        required: m.required,
      });
      return acc;
    }, []);
  }, [selectedModel]);

  // Every kind present, so the hidden file input can accept the right set.
  const mediaAccept = useMemo(() => {
    const kinds = [...new Set(mediaParams.map(p => p.kind))];
    return kinds.map(k => MEDIA_KIND_META[k].accept).join(',');
  }, [mediaParams]);

  // "Reference images" is wrong once a model also takes video or audio.
  const mediaKinds = useMemo(() => [...new Set(mediaParams.map(p => p.kind))], [mediaParams]);

  const mediaSectionTitle = useMemo(
    () => (mediaKinds.length === 1 ? `Reference ${MEDIA_KIND_META[mediaKinds[0]].plural}` : 'Reference media'),
    [mediaKinds]
  );

  // "Drop images / videos and audio files to use as input"
  const mediaDropNoun = useMemo(() => {
    const nouns = mediaKinds.map(k => MEDIA_KIND_META[k].plural);
    if (nouns.length === 0) return 'files';
    if (nouns.length === 1) return nouns[0];
    return `${nouns.slice(0, -1).join(', ')} or ${nouns[nouns.length - 1]}`;
  }, [mediaKinds]);

  // Count params the model batches natively — the Batch chip replaces them so
  // the two controls can't silently multiply each other.
  const nativeCountParams = useMemo(
    () => new Set(['num_images', 'num_outputs', 'batch_size', 'num_samples', 'n']),
    []
  );

  // Only numeric/boolean params that ACTUALLY exist on this model show in Advanced
  const advancedParams = useMemo(() => {
    if (!selectedModel?.parameters) return [];
    return selectedModel.parameters.filter(p => {
      if (p.is_private) return false;
      const dt = p.data_type;
      const n = p.parameter_name.toLowerCase();
      const isNumeric = dt === 'integer' || dt === 'number' || dt === 'float';
      const isBoolean = dt === 'boolean';
      // Classifier, not the name test: this branch only admits numeric/boolean,
      // where classifyMediaParam can never match, so the `audio`/
      // `generate_audio`/`sound` booleans stay IN — while `image_quality`
      // (integer, previously excluded by name and rendered nowhere) finally
      // shows up as the number it is.
      if (classifyMediaParam(p) || n.includes('webhook')) return false;
      if (nativeCountParams.has(n)) return false; // owned by the Batch chip
      return isNumeric || isBoolean;
    });
  }, [selectedModel, nativeCountParams]);

  /* ── Dimension config ─────────────────────────────────────────── */

  const dimensionConfig = useMemo(() => {
    const dp = selectedModel?.resolution?.dimension_presets;
    if (dp?.ratios && dp?.lookup) return dp;
    return null;
  }, [selectedModel]);

  const availableRatios = dimensionConfig?.ratios || [];
  const availableQualities = dimensionConfig?.qualities || [];
  const hasQualitySelector = availableQualities.length > 1;

  const aspectRatioParam = useMemo(() => {
    if (dimensionConfig) return null;
    return selectParams.find(p => p.parameter_name.toLowerCase() === 'aspect_ratio');
  }, [dimensionConfig, selectParams]);

  const activeRatio = settings?.ratio || dimensionConfig?.default_ratio || availableRatios[0] || null;
  const activeQuality = settings?.quality || dimensionConfig?.default_quality || availableQualities[0] || null;

  /* ── Condition rules ──────────────────────────────────────────── */

  const { disabledParams, restrictedValues } = useMemo(() => {
    if (!selectedModel?.parameters) return { disabledParams: {}, restrictedValues: {} };
    const disabled = {};
    const restricted = {};
    selectedModel.parameters.forEach(param => {
      const rule = parseConditionRule(param.condition_rule);
      if (!rule) return;
      const val = textInputs?.[param.parameter_name] || uploadedImages?.[param.parameter_name];
      if (rule.mutually_exclusive_with && hasValue(val)) {
        rule.mutually_exclusive_with.forEach(ep => { disabled[ep] = { reason: `Cannot be used with "${param.parameter_title || param.parameter_name}"` }; });
      }
      if (rule.restricts_when_present && hasValue(val)) {
        Object.entries(rule.restricts_when_present).forEach(([tp, r]) => {
          restricted[tp] = { allowedValues: parseAllowedValues(r.allowed_values), reason: r.reason, sourceParam: param.parameter_name };
        });
      }
      if (rule.requires_min_count) {
        for (const [depParam, minCount] of Object.entries(rule.requires_min_count)) {
          const depVal = uploadedImages?.[depParam];
          const count = Array.isArray(depVal) ? depVal.length : 0;
          if (count < Number(minCount)) {
            disabled[param.parameter_name] = { reason: rule.reason || `Requires at least ${minCount} ${depParam.replace(/_/g, ' ')}` };
          }
        }
      }
    });
    return { disabledParams: disabled, restrictedValues: restricted };
  }, [selectedModel, textInputs, uploadedImages]);

  // Auto-correct restricted values
  useEffect(() => {
    Object.entries(restrictedValues).forEach(([paramName, r]) => {
      const cur = textInputs?.[paramName];
      if (!cur || !r.allowedValues.length) return;
      if (!r.allowedValues.map(String).includes(String(cur))) {
        setTextInputs(prev => ({ ...prev, [paramName]: r.allowedValues[0] }));
      }
    });
  }, [restrictedValues]); // eslint-disable-line

  /* ── Validation — and, crucially, WHY it's blocked ──────────────── */

  const missing = useMemo(() => {
    const out = [];
    if (!selectedModel) { out.push('a model'); return out; }

    const reqText = textParams.filter(p => Number(p.is_required) === 1 || p.is_required === true);
    if (reqText.length > 0) {
      reqText.filter(p => !filled(textInputs?.[p.parameter_name]))
        .forEach(p => out.push(formatLabel(p.parameter_name, p.parameter_title)));
    } else if (textParams.length > 0 && !textParams.some(p => filled(textInputs?.[p.parameter_name]))) {
      out.push('a prompt');
    }

    mediaParams.filter(p => p.required).forEach(p => {
      const val = uploadedImages?.[p.paramName];
      const ok = val && (Array.isArray(val) ? val.length > 0 : true);
      if (!ok) out.push(p.title);
    });

    return out;
  }, [selectedModel, textParams, mediaParams, textInputs, uploadedImages]);

  const canGenerate = !!selectedModel && !isGenerating && !loadingDetails && missing.length === 0;

  /* ── Image upload handlers ────────────────────────────────────── */

  const getImagesForParam = useCallback((paramName) => {
    const val = uploadedImages?.[paramName];
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  }, [uploadedImages]);

  const uploadOne = useCallback(async (file, param) => {
    let f = file;
    // Only images are compressible — running this on a video or audio file
    // wastes CPU and mislabels the result.
    if (param.kind === 'image' && file.type.startsWith('image/') && file.size > 10 * 1024 * 1024) {
      f = await imageCompression(file, { maxSizeMB: 10, useWebWorker: true, alwaysKeepResolution: true });
    }
    const { url } = await uploadMediaFile(f, param.kind);
    setUploadedImages(prev => {
      const cur = prev || {};
      if (param.isArray) {
        const arr = Array.isArray(cur[param.paramName]) ? cur[param.paramName] : (cur[param.paramName] ? [cur[param.paramName]] : []);
        if (arr.length >= param.max) return cur;
        return { ...cur, [param.paramName]: [...arr, url] };
      }
      return { ...cur, [param.paramName]: url };
    });
    return url;
  }, [setUploadedImages]);

  /** Handles multi-select and multi-file drops; single-value slots take the first file. */
  const handleFiles = useCallback(async (fileList, param) => {
    if (!param) return;
    const all = Array.from(fileList || []).filter(Boolean);
    if (!all.length) return;

    const meta = MEDIA_KIND_META[param.kind];
    // Only files matching THIS slot's kind. Previously a mismatched file was
    // dropped with no toast and no error — the user just saw nothing happen.
    const files = all.filter(f => mediaKindOfFile(f) === param.kind);
    let rejected = all.length - files.length;

    if (!files.length) {
      showNotification?.(`"${param.title}" takes ${meta.plural} — that file isn’t ${meta.article} ${meta.label}`, 'warning');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Respect the slot's capacity BEFORE uploading. uploadOne silently no-ops
    // when the array is already full, so without this the extras would be
    // pushed to S3 and still counted as "added".
    // Single-value slots always accept one file — dropping onto a filled slot
    // REPLACES it, which is the only way to swap a single input (the Add button
    // is hidden once it is filled).
    const used = getImagesForParam(param.paramName).length;
    const room = param.isArray ? Math.max(0, param.max - used) : 1;
    if (room === 0) {
      showNotification?.(`"${param.title}" is full (${param.max} max)`, 'warning');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const slice = files.slice(0, room);
    rejected += files.length - slice.length;

    setUploading(param.paramName);
    let ok = 0;
    let lastError = null;
    for (const f of slice) {
      try { await uploadOne(f, param); ok++; }
      catch (e) { lastError = e; }
    }
    setUploading(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (ok === 0) {
      showNotification?.(lastError?.message || 'Upload failed', 'error');
    } else {
      const noun = ok > 1 ? meta.plural : meta.label;
      showNotification?.(
        rejected > 0 ? `${ok} ${noun} added · ${rejected} skipped` : `${ok > 1 ? ok + ' ' : ''}${noun} added`,
        rejected > 0 ? 'warning' : 'success'
      );
    }
  }, [uploadOne, showNotification, getImagesForParam]);

  const handleRemoveImage = useCallback((paramName, idx) => {
    setUploadedImages(prev => {
      if (!prev) return null;
      const cur = { ...prev };
      const val = cur[paramName];
      if (Array.isArray(val)) {
        const next = val.filter((_, i) => i !== idx);
        if (next.length > 0) cur[paramName] = next;
        else delete cur[paramName];
      } else { delete cur[paramName]; }
      return Object.keys(cur).length ? cur : null;
    });
  }, [setUploadedImages]);

  /**
   * Pick the slot a loose file (pasted, or dropped on the panel rather than on
   * a specific slot) should go to: first slot of the FILE'S OWN kind that has
   * room, else any slot of that kind, else null. Kind-matching matters now
   * that a model can expose image + video + audio slots at once — Seedance 2.5
   * and MiniMax H3 both do.
   */
  const pickSlotFor = useCallback((file) => {
    const kind = mediaKindOfFile(file);
    if (!kind) return null;
    const sameKind = mediaParams.filter(p => p.kind === kind && !disabledParams[p.paramName]);
    if (!sameKind.length) return null;
    const withRoom = sameKind.find(p => {
      const cur = getImagesForParam(p.paramName);
      return p.isArray ? cur.length < p.max : cur.length === 0;
    });
    return withRoom || sameKind[0];
  }, [mediaParams, disabledParams, getImagesForParam]);

  /* ── Paste an image straight into the first free slot ───────────── */

  useEffect(() => {
    if (mediaParams.length === 0) return;
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.files || []);
      if (!files.length) return; // plain text paste — let the textarea have it
      const target = pickSlotFor(files[0]);
      if (!target) {
        // Nothing on this model accepts that kind. Say so — a silent no-op
        // here reads as "the app is broken".
        showNotification?.(`This model takes ${mediaDropNoun} — that file was skipped`, 'warning');
        return; // let the paste through to whatever else wants it
      }
      e.preventDefault();
      handleFiles(files, target);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [mediaParams, pickSlotFor, handleFiles, showNotification, mediaDropNoun]);

  /* ── Generate (records prompt history) ──────────────────────────── */

  const primaryPrompt = textParams.find(p => p.parameter_name === 'positive_prompt')
    || textParams.find(p => p.parameter_name === 'prompt')
    || textParams[0];
  const secondaryTextParams = textParams.filter(p => p !== primaryPrompt);
  const promptValue = primaryPrompt ? (textInputs[primaryPrompt.parameter_name] ?? '') : '';
  const promptText = typeof promptValue === 'string' ? promptValue : String(promptValue ?? '');
  const promptMax = 2500;

  const doGenerate = useCallback(() => {
    if (!selectedModel) { showNotification?.('Pick a model first — use "Change" at the top of this panel', 'warning'); return; }
    if (loadingDetails) { showNotification?.('Still loading this model…', 'info'); return; }
    if (isGenerating) return;
    if (!canGenerate) {
      showNotification?.(`Still needed: ${missing.join(', ')}`, 'warning');
      return;
    }
    if (promptText.trim()) setHistory(pushPromptHistory(promptText));
    onGenerate();
  }, [selectedModel, loadingDetails, isGenerating, canGenerate, missing, promptText, onGenerate, showNotification]);

  /* ── ⌘/Ctrl+Enter fires Generate ────────────────────────────────
     Bound to window so it works from the results pane too, but ignored when
     focus sits in some other screen's field (model search, detail modal). */

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return;
      const active = document.activeElement;
      const inPanel = !active || active === document.body || rootRef.current?.contains(active);
      if (!inPanel) return;
      e.preventDefault();
      doGenerate();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doGenerate]);

  /* A drag that leaves the window never fires dragleave on the panel, so the
     drop overlay would stick until the next drag. Clear it globally. */
  useEffect(() => {
    const clear = () => setDragParam(null);
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    return () => { window.removeEventListener('dragend', clear); window.removeEventListener('drop', clear); };
  }, []);

  /* ── Enhance ────────────────────────────────────────────────────── */

  const cat = getModelCategory(selectedModel?.model_type);
  const catC = catColors(cat);

  const handleEnhance = useCallback(async () => {
    if (!primaryPrompt) return;
    setEnhancing(true);
    try {
      const kind = cat === 'Video' ? 'video' : cat === 'Text' ? 'text' : 'image';
      const better = await enhancePrompt(promptText, kind);
      setTextInputs(prev => ({ ...prev, [primaryPrompt.parameter_name]: better }));
      showNotification?.('Prompt enhanced — the original is in Recent', 'success');
      setHistory(pushPromptHistory(promptText));
    } catch (err) {
      showNotification?.(err?.message || 'Enhance failed', 'error');
    } finally {
      setEnhancing(false);
    }
  }, [primaryPrompt, promptText, cat, setTextInputs, showNotification]);

  /* ── Cost estimate ───────────────────────────────────────────── */

  const costDisplay = useMemo(() => {
    if (!selectedModel) return '—';
    const base = Number(selectedModel.lowest_cost || selectedModel.average_cost || 0);
    if (base <= 0) return '—';
    const total = base * Math.max(1, Number(batchCount) || 1);
    return `~${formatCost ? formatCost(total) : total.toPrecision(2)} URO`;
  }, [selectedModel, batchCount, formatCost]);

  const balanceTone = balance == null ? 'var(--text-3)' : Number(balance) > 5 ? '#10B981' : Number(balance) > 1 ? '#F59E0B' : '#F5576C';

  /* ── Drag & drop ────────────────────────────────────────────────── */

  const dropTargetFor = useCallback((param) => ({
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); setDragParam(param.paramName); },
    onDragLeave: (e) => { e.preventDefault(); e.stopPropagation(); setDragParam(null); },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      setDragParam(null);
      // Ignore drops while an upload is in flight: handleFiles reads the slot's
      // current count once, so two overlapping batches would each claim the
      // same free capacity and over-report what actually landed.
      if (uploading) return;
      if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files, param);
    },
  }), [handleFiles, uploading]);

  // Dropping anywhere on the panel routes to the first slot of a matching kind.
  const panelDrop = {
    onDragOver: (e) => { if (mediaParams.length) { e.preventDefault(); setDragParam(dragParam || '__panel__'); } },
    onDragLeave: (e) => { if (e.currentTarget === e.target) setDragParam(null); },
    onDrop: (e) => {
      if (!mediaParams.length || !e.dataTransfer?.files?.length) return;
      e.preventDefault();
      setDragParam(null);
      const first = e.dataTransfer.files[0];
      const target = pickSlotFor(first);
      if (!target) {
        showNotification?.(`This model takes ${mediaDropNoun} — that file was skipped`, 'warning');
        return;
      }
      handleFiles(e.dataTransfer.files, target);
    },
  };

  const totalUploaded = Object.values(uploadedImages || {}).flatMap(v => Array.isArray(v) ? v : [v]).filter(Boolean).length;

  /* ────────────────────────────────────────────────────────────── */

  return (
    <div
      ref={rootRef}
      {...panelDrop}
      style={{ height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative', ...style }}
    >

      {/* Panel-wide drop hint */}
      {dragParam === '__panel__' && (
        <div style={{ position: 'absolute', inset: 8, zIndex: 40, borderRadius: 14, border: '2px dashed rgba(255,126,103,.6)', background: 'rgba(255,126,103,.08)', display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#FFC2B5' }}>Drop {mediaDropNoun} to use as input</span>
        </div>
      )}

      {/* Scrollable form area */}
      <div className="scroll-y" style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>

        {/* ── Model selector — the whole card is the button ───────────── */}
        <div>
          <SectionLabel>Model</SectionLabel>
          <button
            onClick={() => onOpenModelSelector?.()}
            title="Browse and switch models"
            className="card"
            style={{ width: '100%', textAlign: 'left', padding: 12, display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)' }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,126,103,.4)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = '')}
          >
            {(selectedModel?.model_thumbnail_image_url || selectedModel?.model_cover_image_url) ? (
              <img
                src={selectedModel.model_thumbnail_image_url || selectedModel.model_cover_image_url}
                alt=""
                style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover', flex: '0 0 52px', border: '1px solid var(--border)' }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="ph" style={{ width: 52, height: 52, borderRadius: 10, flex: '0 0 52px', border: `1px solid ${catC.ring}`, '--ph-from': catC.from, '--ph-to': catC.to }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row" style={{ gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {modelsLoading ? 'Loading…' : (selectedModel?.model_name || selectedModel?.name || 'Select a model')}
                </span>
                {selectedModel && <span className="chip" style={{ height: 18, fontSize: 10, color: catC.label, borderColor: catC.ring, flexShrink: 0 }}>{cat}</span>}
              </div>
              {selectedModel?.lowest_cost != null && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  from ${Number(selectedModel.lowest_cost).toPrecision(2)} per output
                </div>
              )}
            </div>
            <span className="btn btn-ghost btn-sm" style={{ flexShrink: 0, pointerEvents: 'none' }}>Change</span>
          </button>
        </div>

        {/* ── Primary prompt ──────────────────────────────────────────── */}
        {primaryPrompt && (
          <div>
            <SectionLabel right={`${promptText.length} / ${promptMax}`}>
              {formatLabel(primaryPrompt.parameter_name, primaryPrompt.parameter_title)}
            </SectionLabel>
            <div className="card" style={{ padding: 12, minHeight: 118 }}>
              <textarea
                value={promptText}
                onChange={e => setTextInputs(prev => ({ ...prev, [primaryPrompt.parameter_name]: e.target.value }))}
                placeholder={`Describe ${cat === 'Video' ? 'the video' : cat === 'Text' ? 'what you want' : 'the image'}…  (⌘/Ctrl + Enter to generate)`}
                maxLength={promptMax}
                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 14, lineHeight: 1.55, resize: 'none', fontFamily: 'inherit', minHeight: 92 }}
              />
            </div>
            <div className="row" style={{ marginTop: 8, gap: 6, flexWrap: 'wrap' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleEnhance}
                disabled={enhancing || !promptText.trim()}
                title="Rewrite your prompt with more detail (uses a small LLM call)"
                style={{ opacity: enhancing || !promptText.trim() ? .5 : 1 }}
              >
                {enhancing ? 'Enhancing…' : '✦ Enhance'}
              </button>

              {/* Recent prompts */}
              <div style={{ position: 'relative' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setHistoryOpen(v => !v)} disabled={history.length === 0}
                  title={history.length ? 'Reuse a recent prompt' : 'No prompts yet'}
                  style={{ opacity: history.length === 0 ? .45 : 1, gap: 6 }}>
                  <HistoryIcon s={12} /> Recent
                </button>
                {historyOpen && history.length > 0 && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setHistoryOpen(false)} />
                    <div style={{ position: 'absolute', top: '110%', left: 0, width: 320, maxWidth: '80vw', maxHeight: 300, overflowY: 'auto', background: 'rgba(12,12,20,.98)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 200 }}>
                      {history.map((h, i) => (
                        <button key={i}
                          onClick={() => { setTextInputs(prev => ({ ...prev, [primaryPrompt.parameter_name]: h })); setHistoryOpen(false); }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,.05)', color: 'var(--text-2)', fontSize: 12, lineHeight: 1.45, cursor: 'pointer' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.06)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{h}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => setTextInputs(prev => ({ ...prev, [primaryPrompt.parameter_name]: '' }))}
                disabled={!promptText}
                title="Clear the prompt">Clear</button>
            </div>
          </div>
        )}

        {/* ── Secondary text params (negative prompt, etc.) ───────────── */}
        {secondaryTextParams.map(p => {
          const isDisabled = !!disabledParams[p.parameter_name];
          const raw = textInputs[p.parameter_name];
          return (
            <div key={p.parameter_name} style={{ opacity: isDisabled ? .4 : 1 }}>
              <SectionLabel>
                {formatLabel(p.parameter_name, p.parameter_title)}
                {p.is_required ? ' *' : ''}
              </SectionLabel>
              <div className="card" style={{ padding: 10 }}>
                <textarea
                  value={typeof raw === 'string' ? raw : (raw ?? '')}
                  onChange={e => !isDisabled && setTextInputs(prev => ({ ...prev, [p.parameter_name]: e.target.value }))}
                  disabled={isDisabled}
                  placeholder={p.description || ''}
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 13, lineHeight: 1.5, resize: 'none', fontFamily: 'inherit', minHeight: 48 }}
                />
              </div>
              {isDisabled && disabledParams[p.parameter_name]?.reason && (
                <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>{disabledParams[p.parameter_name].reason}</div>
              )}
            </div>
          );
        })}

        {/* ── Select params ──────────────────────────────────────────── */}
        {selectParams.filter(p => p.parameter_name.toLowerCase() !== 'aspect_ratio').map(p => {
          const isDisabled = !!disabledParams[p.parameter_name];
          const curVal = textInputs[p.parameter_name] || parseAllowedValues(p.allowed_values)[0] || '';
          return (
            <div key={p.parameter_name} style={{ opacity: isDisabled ? .4 : 1 }}>
              <SectionLabel>{formatLabel(p.parameter_name, p.parameter_title)}</SectionLabel>
              <SelectDropdown
                param={p}
                value={curVal}
                onChange={v => setTextInputs(prev => ({ ...prev, [p.parameter_name]: v }))}
                disabled={isDisabled}
                restricted={restrictedValues[p.parameter_name]}
              />
            </div>
          );
        })}

        {/* ── Media upload slots — click, drop or paste ───────────────── */}
        {mediaParams.length > 0 && (
          <div>
            <SectionLabel right={totalUploaded ? `${totalUploaded} uploaded` : 'drop or paste'}>
              {mediaParams.length === 1
                ? formatLabel(mediaParams[0].paramName, mediaParams[0].title)
                : mediaSectionTitle}
            </SectionLabel>
            {mediaParams.map(param => {
              const items = getImagesForParam(param.paramName);
              const isDisabled = !!disabledParams[param.paramName];
              const canAdd = param.isArray ? items.length < param.max : items.length === 0;
              const isDropTarget = dragParam === param.paramName;
              const meta = MEDIA_KIND_META[param.kind];
              // Audio has no meaningful thumbnail, so give it a full-width row
              // with a real player instead of a square in the 4-up grid.
              const isAudio = param.kind === 'audio';
              return (
                <div key={param.paramName}
                  {...(isDisabled ? {} : dropTargetFor(param))}
                  style={{
                    marginBottom: mediaParams.length > 1 ? 10 : 0, opacity: isDisabled ? .4 : 1,
                    borderRadius: 12, padding: isDropTarget ? 6 : 0,
                    outline: isDropTarget ? '2px dashed rgba(255,126,103,.6)' : 'none',
                    background: isDropTarget ? 'rgba(255,126,103,.08)' : 'transparent',
                    transition: 'background .12s',
                  }}
                >
                  {mediaParams.length > 1 && (
                    <div style={{ fontSize: 10, color: 'var(--text-4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      {param.title}{param.required ? ' *' : ''}{param.isArray ? ` · up to ${param.max}` : ''}
                    </div>
                  )}
                  <div style={isAudio
                    ? { display: 'flex', flexDirection: 'column', gap: 8 }
                    : { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {items.map((url, i) => (
                      <div key={i} style={{
                        ...(isAudio
                          ? { padding: 8, display: 'flex', alignItems: 'center', gap: 8 }
                          : { aspectRatio: 1 }),
                        borderRadius: 10, position: 'relative', overflow: 'hidden', border: '1px solid var(--border)',
                      }}>
                        {param.kind === 'image' && (
                          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        )}
                        {param.kind === 'video' && (
                          // muted+playsInline so the thumbnail can render a frame
                          // without autoplay policies blocking it; controls on
                          // hover would fight the 4-up grid, so click-to-play.
                          <video
                            src={url}
                            muted
                            playsInline
                            preload="metadata"
                            controls
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
                          />
                        )}
                        {isAudio && (
                          <audio src={url} controls preload="metadata" style={{ width: '100%', height: 32 }} />
                        )}
                        <button onClick={() => handleRemoveImage(param.paramName, i)} title="Remove"
                          style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.7)', color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 10, zIndex: 1 }}>×</button>
                      </div>
                    ))}
                    {canAdd && !isDisabled && (
                      <button
                        onClick={() => {
                          fileTargetParamRef.current = param;
                          // Narrow the picker to THIS slot's kind, otherwise a
                          // model with image+video+audio slots offers all three
                          // and then rejects the wrong ones after selection.
                          if (fileInputRef.current) fileInputRef.current.accept = meta.accept;
                          fileInputRef.current?.click();
                        }}
                        disabled={!!uploading}
                        title={`Click to browse ${meta.plural}, or drag files onto this panel`}
                        style={{
                          ...(isAudio ? { height: 40 } : { aspectRatio: 1 }),
                          borderRadius: 10, border: '1px dashed var(--border-2)', background: 'rgba(255,255,255,.02)', color: 'var(--text-3)', cursor: 'pointer', display: 'flex', flexDirection: isAudio ? 'row' : 'column', alignItems: 'center', justifyContent: 'center', gap: isAudio ? 8 : 4, fontSize: 10, textAlign: 'center', padding: 4,
                        }}
                      >
                        <UploadIcon s={14} />
                        {uploading === param.paramName ? 'Uploading…' : `Add ${meta.label}`}
                      </button>
                    )}
                  </div>
                  {isDisabled && disabledParams[param.paramName]?.reason && (
                    <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>{disabledParams[param.paramName].reason}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Aspect ratio ──────────────────────────────────────────── */}
        {(dimensionConfig && availableRatios.length > 0) || aspectRatioParam ? (
          <div>
            <SectionLabel>Aspect ratio</SectionLabel>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {dimensionConfig
                ? availableRatios.map(r => (
                    <button key={r} onClick={() => setSettings(s => ({ ...s, ratio: r }))} style={{
                      height: 32, padding: '0 12px', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
                      border: `1px solid ${r === activeRatio ? 'rgba(255,126,103,.45)' : 'var(--border)'}`,
                      background: r === activeRatio ? 'rgba(255,126,103,.10)' : 'transparent',
                      color: r === activeRatio ? '#FFC2B5' : 'var(--text-2)', fontSize: 12, fontWeight: 600,
                    }}>{r}</button>
                  ))
                : parseAllowedValues(aspectRatioParam.allowed_values).map(r => (
                    <button key={r} onClick={() => setTextInputs(prev => ({ ...prev, [aspectRatioParam.parameter_name]: r }))} style={{
                      height: 32, padding: '0 12px', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
                      border: `1px solid ${textInputs[aspectRatioParam.parameter_name] === r ? 'rgba(255,126,103,.45)' : 'var(--border)'}`,
                      background: textInputs[aspectRatioParam.parameter_name] === r ? 'rgba(255,126,103,.10)' : 'transparent',
                      color: textInputs[aspectRatioParam.parameter_name] === r ? '#FFC2B5' : 'var(--text-2)', fontSize: 12, fontWeight: 600,
                    }}>{r}</button>
                  ))
              }
            </div>
          </div>
        ) : null}

        {/* ── Quality ─────────────────────────────────────────────────── */}
        {dimensionConfig && hasQualitySelector && (
          <div>
            <SectionLabel>Quality</SectionLabel>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {availableQualities.map(q => (
                <button key={q} onClick={() => setSettings(s => ({ ...s, quality: q }))} style={{
                  height: 30, padding: '0 12px', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
                  border: `1px solid ${q === activeQuality ? 'rgba(99,102,241,.4)' : 'var(--border)'}`,
                  background: q === activeQuality ? 'rgba(99,102,241,.1)' : 'transparent',
                  color: q === activeQuality ? '#A5B4FC' : 'var(--text-3)', fontSize: 12, fontWeight: 600,
                }}>{q}</button>
              ))}
            </div>
          </div>
        )}

        {/* ── Advanced settings ───────────────────────────────────────── */}
        {advancedParams.length > 0 && (
          <div>
            <button className="row" onClick={() => setShowAdvanced(v => !v)} style={{ background: 'transparent', border: 'none', color: 'var(--text-2)', padding: 0, cursor: 'pointer', gap: 6, fontSize: 12, fontWeight: 600 }}>
              <span style={{ display: 'inline-block', transform: showAdvanced ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .2s' }}>
                <CaretIcon s={10} />
              </span>
              Advanced settings
              <span style={{ fontSize: 10, color: 'var(--text-4)', fontWeight: 400 }}>({advancedParams.length})</span>
            </button>
            {showAdvanced && (
              <div style={{ marginTop: 10, padding: 14, borderRadius: 12, background: 'rgba(255,255,255,.02)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {advancedParams.map(p => {
                    const rawVal = textInputs[p.parameter_name];
                    const currentVal = rawVal !== undefined ? rawVal : (p.default_value ?? '');
                    const isDisabled = !!disabledParams[p.parameter_name];
                    const min = p.allowed_min_value != null ? Number(p.allowed_min_value) : null;
                    const max = p.allowed_max_value != null ? Number(p.allowed_max_value) : null;
                    const numVal = Number(currentVal) || 0;
                    const setVal = (v) => setTextInputs(prev => ({ ...prev, [p.parameter_name]: v }));

                    const isSeed = p.parameter_name.toLowerCase() === 'seed';
                    const display = isSeed && (currentVal === -1 || currentVal === '-1' || currentVal === '') ? 'random' : String(currentVal);

                    if (p.data_type === 'boolean') {
                      const boolVal = currentVal === true || currentVal === 'true' || currentVal === 1;
                      return (
                        <div key={p.parameter_name} style={{ opacity: isDisabled ? .4 : 1 }}>
                          <div style={{ fontSize: 10, color: 'var(--text-4)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 4 }}>
                            {formatLabel(p.parameter_name, p.parameter_title)}
                          </div>
                          <button onClick={() => !isDisabled && setVal(!boolVal)} style={{
                            height: 32, padding: '0 10px', borderRadius: 8,
                            background: boolVal ? 'rgba(255,126,103,.1)' : 'rgba(255,255,255,.04)',
                            border: `1px solid ${boolVal ? 'rgba(255,126,103,.35)' : 'var(--border)'}`,
                            color: boolVal ? '#FFC2B5' : 'var(--text-2)', fontSize: 12, cursor: 'pointer',
                          }}>
                            {boolVal ? 'On' : 'Off'}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div key={p.parameter_name} style={{ opacity: isDisabled ? .4 : 1 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-4)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 4 }}>
                          {formatLabel(p.parameter_name, p.parameter_title)}
                        </div>
                        <div className="row" style={{ height: 32, padding: '0 8px', borderRadius: 8, background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-2)', gap: 4 }}>
                          <input
                            value={display}
                            disabled={isDisabled}
                            onChange={e => {
                              const v = e.target.value;
                              if (isSeed && v.toLowerCase() === 'random') { setVal(-1); return; }
                              const num = p.data_type === 'float' ? parseFloat(v) : parseInt(v, 10);
                              if (!isNaN(num)) setVal(num);
                              else if (v === '' || v === '-') setVal(v);
                            }}
                            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-2)', fontSize: 12, fontFamily: 'monospace', minWidth: 0 }}
                          />
                          {isSeed ? (
                            <button onClick={() => !isDisabled && setVal(Math.floor(Math.random() * 2147483647))} title="Roll a new fixed seed"
                              style={{ width: 20, height: 20, borderRadius: 5, background: 'rgba(255,255,255,.06)', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                              <RefreshIcon s={11} />
                            </button>
                          ) : (min != null || max != null) && (
                            <div className="row" style={{ gap: 4, flexShrink: 0 }}>
                              {min != null && (
                                <button onClick={() => !isDisabled && setVal(Math.max(min, numVal - 1))} title={`Minimum ${min}`}
                                  style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(255,255,255,.06)', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 11, display: 'grid', placeItems: 'center' }}>−</button>
                              )}
                              {max != null && (
                                <button onClick={() => !isDisabled && setVal(Math.min(max, numVal + 1))} title={`Maximum ${max}`}
                                  style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(255,255,255,.06)', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 11, display: 'grid', placeItems: 'center' }}>+</button>
                              )}
                            </div>
                          )}
                        </div>
                        {(min != null || max != null) && (
                          <div style={{ fontSize: 10, color: 'var(--text-4)', marginTop: 3 }}>
                            {min != null && max != null ? `${min} – ${max}` : min != null ? `min ${min}` : `max ${max}`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Escape hatch: every parameter this model exposes ────────── */}
        {onOpenSettings && selectedModel && (
          <button className="btn btn-ghost btn-sm" onClick={onOpenSettings}
            title="Open every parameter this model exposes, including ones not shown here"
            style={{ alignSelf: 'flex-start' }}>
            All parameters
            <span style={{ fontSize: 10, color: 'var(--text-4)', fontWeight: 400 }}>
              ({(selectedModel.parameters || []).filter(p => !p.is_private).length})
            </span>
          </button>
        )}

      </div>

      {/* ── Sticky generate footer ──────────────────────────────────── */}
      <div style={{ padding: 16, borderTop: '1px solid var(--border)', background: 'rgba(255,255,255,.02)', flexShrink: 0 }}>

        {/* Batch quantity — one press, N generations */}
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }} title="How many separate generations one press of Generate should start">
            Batch
          </span>
          <div className="row" style={{ gap: 4, padding: 3, borderRadius: 9, background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)' }}>
            {[1, 2, 3, 4].map(n => (
              <button key={n} onClick={() => setBatchCount?.(n)}
                title={n === 1 ? 'One generation' : `${n} generations per press`}
                style={{
                  minWidth: 30, height: 24, borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  background: batchCount === n ? 'var(--grad)' : 'transparent',
                  color: batchCount === n ? '#fff' : 'var(--text-3)',
                }}>×{n}</button>
            ))}
          </div>
        </div>

        <div className="row" style={{ justifyContent: 'space-between', fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
          <span>Estimated cost</span>
          <span className="mono" style={{ color: '#fff', fontWeight: 600 }}>{costDisplay}</span>
        </div>

        <button
          onClick={doGenerate}
          className="btn btn-primary btn-lg"
          style={{ width: '100%', justifyContent: 'center', opacity: (canGenerate && !isGenerating) ? 1 : 0.5, cursor: (canGenerate && !isGenerating) ? 'pointer' : 'not-allowed' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6 7.7 7.7M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/></svg>
          {isGenerating ? 'Generating…' : loadingDetails ? 'Loading model…' : batchCount > 1 ? `Generate ×${batchCount}` : 'Generate'}
        </button>

        {/* Say WHY it's blocked instead of silently greying out */}
        {!canGenerate && !isGenerating && !loadingDetails && missing.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#fbbf24', lineHeight: 1.45 }}>
            Still needed: {missing.join(', ')}
          </div>
        )}

        <div className="row" style={{ marginTop: 10, justifyContent: 'space-between', fontSize: 12, gap: 8 }}>
          <span style={{ color: 'var(--text-3)' }}>
            Balance:{' '}
            <span className="mono" style={{ color: balanceTone, fontWeight: 600 }}>
              {balance != null ? Number(balance).toFixed(4) : '—'} URO
            </span>
          </span>
          <a href="/account" style={{ color: '#FFC2B5', textDecoration: 'none', fontWeight: 600 }}>Top up</a>
        </div>

        {onCloseSheet && (
          <button className="btn btn-ghost btn-sm" onClick={onCloseSheet} style={{ width: '100%', justifyContent: 'center', marginTop: 10, gap: 6 }}>
            <CloseIcon s={12} /> Close controls
          </button>
        )}
      </div>

      {/* Hidden file input — multiple, so one pick can fill an array slot */}
      <input ref={fileInputRef} type="file" accept={mediaAccept || 'image/*'} multiple style={{ display: 'none' }}
        onChange={e => {
          const param = fileTargetParamRef.current;
          if (e.target.files?.length && param) handleFiles(e.target.files, param);
        }}
      />

    </div>
  );
}
